import imageCompression from 'browser-image-compression';
import {
  ImagePickValidationError,
  validateCompressedImageFile,
  validateOriginalImageFile,
} from '../lib/imagePickRules';

const WRITE_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1280,
  useWebWorker: true,
  initialQuality: 0.8,
  fileType: 'image/webp',
};

const AVATAR_OPTIONS = {
  maxSizeMB: 200 / 1024,
  maxWidthOrHeight: 200,
  useWebWorker: true,
  initialQuality: 0.82,
  fileType: 'image/jpeg',
};

function asFile(blobOrFile, originalFile, fallbackName) {
  if (blobOrFile instanceof File) return blobOrFile;
  if (blobOrFile instanceof Blob) {
    const base = (originalFile?.name || fallbackName || 'image').replace(/\.[^.]+$/i, '');
    const mime = blobOrFile.type || 'image/webp';
    const ext = mime.includes('webp') ? 'webp' : 'jpg';
    return new File([blobOrFile], `${base}.${ext}`, { type: mime });
  }
  return originalFile;
}

/**
 * 피드/거래글용 압축 (최대 ~500KB, 긴 변 1280px, 품질 80%).
 * 실패 시 원본 File 반환.
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function compressImage(file) {
  if (!file) return file;
  const pre = validateOriginalImageFile(file);
  if (!pre.ok) throw new ImagePickValidationError(pre.toast);
  try {
    const out = await imageCompression(file, WRITE_OPTIONS);
    const next = asFile(out, file, 'upload');
    const post = validateCompressedImageFile(next);
    if (!post.ok) throw new ImagePickValidationError(post.toast);
    return next;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[Mingo image] compress failed, using original', e);
    }
    return file;
  }
}

/**
 * @param {File[]} files
 * @returns {Promise<File[]>}
 */
export async function compressImages(files) {
  const arr = Array.isArray(files) ? files : [];
  return Promise.all(arr.map((f) => compressImage(f)));
}

/**
 * 프로필 사진 (최대 ~200KB, 200×200 리사이즈).
 * 실패 시 원본 File 반환.
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function compressAvatarImage(file) {
  if (!file) return file;
  const pre = validateOriginalImageFile(file);
  if (!pre.ok) throw new ImagePickValidationError(pre.toast);
  try {
    const out = await imageCompression(file, AVATAR_OPTIONS);
    const next = asFile(out, file, 'avatar');
    const post = validateCompressedImageFile(next);
    if (!post.ok) throw new ImagePickValidationError(post.toast);
    return next;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[Mingo image] avatar compress failed, using original', e);
    }
    return file;
  }
}
